'use client';
import { SectionContent } from "@/types/sections.model";
import { Box, Container, Collapse, Stack, Typography, Button } from "@mui/material";

import React, { useEffect, useRef, useState } from "react";
import ProductCard from "./ProductCard";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import ProductContent from "./ProductContent";
import { useWindowSize } from "@/hook/useWindowSize";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';

const ProductsCarousel: React.FC<{ section: SectionContent<'products-carousel'> }> = ({section}) => {

  const { id, content, items } = section;

  const sectionRef = useRef<HTMLElement | null>(null);
  const swiperRef = useRef(null);
  const dotsRef = useRef(null);

  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const contentRef = useRef<(HTMLDivElement | null)>(null);

  const handleItemClick = (index: number) => {
    setActiveIndex(index === activeIndex ? null : index);
  }

  const handleScrollUp = () => {
    const target = sectionRef.current;
    if (!target) return;

    window.requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    setActiveIndex(null);
  }

  useEffect(() => {
    if (activeIndex === null) return;

    const target = contentRef.current;
    if (!target) return;

    window.requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [activeIndex]);

  const { isMobile } = useWindowSize();
  const slidesPerView = isMobile ? 1 : 3;

  return (
    <Box id={id} ref={sectionRef} bgcolor={'primary.main'}>
      <Container maxWidth="lg">
        <Stack py={48} textAlign={'center'} color={'#FFFFFF'} justifyContent={'center'} alignItems={'center'} gap={12}>
          <Typography variant="h2" fontWeight={700}>{content.title}</Typography>
          <Typography variant="headlineMDMedium">{content.subtitle}</Typography>
        </Stack>
      </Container>

      <Container>
        <Box pb={48}>

          {items && items.length > 0 && (
            <Swiper
              ref={swiperRef}
              slidesPerView={slidesPerView}
              className={`products-carousel-${id}`}
              modules={[Pagination]}
              spaceBetween={32}
              centeredSlides={false}
              centerInsufficientSlides={true}
              allowTouchMove={true}
              draggable={true}
              onBeforeInit={(swiper) => {
                swiperRef.current = swiper;
              }}
              pagination={{
                clickable: true,
                el: dotsRef.current,
              }}
            >
              {items.map((item, index) => (
                <SwiperSlide key={'swiper-slide-' + (item.id ?? `product-${item.title}-${index}`)}>
                  <ProductCard
                    image={item.image}
                    title={item.title}
                    callBack={() => handleItemClick(index)}
                    active={activeIndex === null ? true : activeIndex === index}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          )}

          <Box
            ref={dotsRef}
            sx={{
              marginTop: 26,
              justifyContent: {
                xs: 'center',
                md: 'flex-start',
              },
              display: 'flex',
              gap: 1,
              '& .swiper-pagination-bullet': {
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: 'white',
                opacity: 0.25,
                transition: 'all 0.3s ease',
              },
              '& .swiper-pagination-bullet-active': {
                opacity: 1,
              },
            }}
          />
        </Box>
      </Container>

      <Box ref={contentRef} bgcolor={'white'}>
        {items.length > 0 && items.map((item, index) => (
          <Collapse
            key={'product-content-' + (item.id ?? `product-${item.title}-${index}`)}
            in={activeIndex === index}
            timeout={{
              exit: 1000,
            }}  
          >
            <ProductContent item={item} />
          </Collapse>
        ))}
      </Box>

      <Collapse in={activeIndex !== null}>
        <Box
          display={'flex'}
          justifyContent={'center'}
          alignItems={'center'}
          sx={{
            pb: { xs: 32, lg: 48 },
            backgroundColor: 'secondary.main'
          }}
        >
          <Button
            variant="outlined"
            endIcon={<ArrowUpwardIcon />}
            onClick={handleScrollUp}
          >
            Scopri un’altra routine
          </Button>
        </Box>
      </Collapse>
    </Box>
  );
}

export default ProductsCarousel;